<template>
  <form-component class="status-reply-form" @submit.prevent="addReply">
    <input type="hidden" :value="parentReplyId">
    <div
      class="validation-group"
      :class="{ 'validation-group--invalid': $v.body.$invalid }"
    >
      <validation-error
        ref="body"
        :model="body"
        :validator="$v.body"
      />
      <mention-box
        ref="mentionBox"
        :mentionable="status"
        mentionable-type="USER_PROFILE_STATUS_REPLY"
        placeholder="Write a reply..."
        @input="handleReplyBody"
      />
    </div>
    <div class="status-reply-form__prompt disclaimer-text">
      Please&nbsp;
      <a href="https://health-union.com/community-rules/"
         target="_blank"
         rel="noopener noreferrer nofollow"
      >read our rules</a>&nbsp;
      before posting.
    </div>
    <loading-button
      type="submit"
      :loading="tryingToReply"
      button-type="reply"
    >
      Post Reply
    </loading-button>
  </form-component>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import MentionBox from '@/components/Mention/MentionBox.vue';
import LoadingButton from '@/components/Global/LoadingButton.vue';
import ValidationError from '@/components/Global/ValidationError.vue';
import { REPLY_TO_USER_STATUS } from '@/graphql/mutations/status-mutation';
import { USER_STATUS_REPLIES_FRAGMENT } from '@/graphql/fragments/user-fragments';
import { notJustMention } from '@/utils/utils';
import { mapGetters } from 'vuex';

export default {
  name: 'StatusReplyForm',
  components: {
    MentionBox,
    LoadingButton,
    ValidationError,
  },
  props: {
    status: {
      type: Object,
      required: true,
    },
    reply: {
      type: Object,
      default: null,
    },
    parentReplyId: {
      type: String,
      default: null,
    },
    // This prop is necessary to query the cache based on the original query in StatusHistory
    limit: {
      type: Number,
      default: null,
    },
    isChildReply: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      body: '',
      tryingToReply: false,
      submissionAttempted: false,
    };
  },
  validations: {
    body: {
      required,
      notJustMention,
    },
  },
  computed: {
    ...mapGetters([
      'userIsLoggedIn',
      'userIsUnverified',
    ]),
    userSlug() {
      return this.$route.name === 'memberProfileStatus'
        ? this.$route.params.slug
        : this.$store.state.currentUser.user.slug;
    },
    inReplyToId() {
      if (this.isChildReply) {
        return this.reply.inReplyTo.id;
      }
      if (this.reply) {
        return this.reply.id;
      }
      return null;
    },
  },
  watch: {
    body(to, from) {
      if (to !== from && this.submissionAttempted) {
        this.$refs.body.checkErrors();
      }
    },
  },
  methods: {
    handleReplyBody(text) {
      this.body = text;
    },
    addReply() {
      this.tryingToReply = true;
      this.$v.$touch();
      this.$refs.body.checkErrors();
      this.submissionAttempted = true;
      if (this.$v.$invalid) {
        this.tryingToReply = false;
        return;
      }

      if (this.userIsLoggedIn && this.userIsUnverified) {
        this.tryingToReply = false;
        this.$store.dispatch('openVerificationPrompt', { dialogHeading: 'Verify your account' });
        return;
      }

      this.$apollo.mutate({
        mutation: REPLY_TO_USER_STATUS,
        variables: {
          body: this.body,
          inReplyToId: this.inReplyToId,
          userProfileStatusId: this.status.id,
        },
        update: (cache, { data: { createUserProfileStatusReply } }) => {
          // do not update replies that are pending moderation
          if (createUserProfileStatusReply.isPendingModeration) {
            this.$store.dispatch('addToastNotification', {
              toastType: 'info',
              description: 'This reply requires an extra set of eyes. We\'ll review and get back to you.',
              type: 'Status Update',
            });
            return;
          }

          const fragmentId = `UserProfileStatus:${this.status.id}`;

          // read from cache just to get the replies without needing to match
          // every feild of UserProfileStatus
          const data = cache.readFragment({
            id: fragmentId,
            fragment: USER_STATUS_REPLIES_FRAGMENT,
            fragmentName: 'UserStatusRepliesFragment',
          });

          // update cache data, pushing new reply into the replies array
          data.replies.push(createUserProfileStatusReply);

          // write only the new replies array to cache
          cache.writeFragment({
            id: fragmentId,
            fragment: USER_STATUS_REPLIES_FRAGMENT,
            fragmentName: 'UserStatusRepliesFragment',
            data,
          });

          this.$store.dispatch('addToastNotification', {
            toastType: 'success',
            description: 'Your reply was posted!',
            type: 'Status Update',
          });
        },
      })
        .then(({ data: { createUserProfileStatusReply } }) => {
          this.$refs.mentionBox.clear();
          if (createUserProfileStatusReply.isPendingModeration) {
            return true;
          }
          this.$emit('submitted');
          return true;
        })
        .catch(() => {
          this.$store.dispatch('addGenericErrorNotification');
        }).finally(() => {
          this.tryingToReply = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '@/stylesheets/components/_status-reply-form';
</style>

<docs>

A form that allows the user to respond to user profile status updates.
Displayed dynamically by StatusEngagementBar.
Width is constrained by the parent StatusEngagementBar.

</docs>
